import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faStop, faHeartbeat, faRedo, faShareAlt, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { Paper, Button, Typography, Divider, Grid, Box } from '@mui/material';
import ArrestTimer from './ArrestTimer';
import EpinephrineTimer from './EpinephrineTimer';
import PulseCheckTimer from './PulseCheckTimer';
import Actions from './Actions';

export default function CardiacArrestTimer() {
    const [isRunning, setIsRunning] = useState(() => {
        const savedRunning = localStorage.getItem('isRunning');
        return savedRunning ? JSON.parse(savedRunning) : false;
    });
    const [isCPR, setIsCPR] = useState(() => {
        const savedCPR = localStorage.getItem('isCPR');
        return savedCPR ? JSON.parse(savedCPR) : false;
    });
    const [isEpi, setIsEpi] = useState(() => {
        const savedEpi = localStorage.getItem('isEPI');
        return savedEpi ? JSON.parse(savedEpi) : false;
    });

    const [timeline, setTimeline] = useState([]);
    const [reset, setReset] = useState(false);

    const handleROSC = () => {
        console.log("ROSC achieved");
    };

    useEffect(() => {
        localStorage.setItem('isRunning', isRunning);
    }, [isRunning]);

    useEffect(() => {
        localStorage.setItem('isCPR', isCPR);
    }, [isCPR]);

    const addTimelineEvent = (action) => {
        const timestamp = new Date().toLocaleTimeString();
        setTimeline((prevTimeline) => [...prevTimeline, { action, timestamp }]);
    };

    const startTimer = () => {
        setIsRunning(true);
        addTimelineEvent("Start Code");
    };

    const stopTimer = () => {
        setIsRunning(false);
        addTimelineEvent("Stop Code");
    };

    const startCPR = () => {
        setIsCPR(true);
        addTimelineEvent("Start CPR");
    };

    const stopCPR = () => {
        setIsCPR(false);
        addTimelineEvent("Stop CPR");
    };

    const resetTimer = () => {
        setIsRunning(false);
        setIsCPR(false);
        setTimeline([]);
        setReset((prevReset) => !prevReset);  // Toggle reset to trigger in child components
        localStorage.removeItem('isRunning');
        localStorage.removeItem('isCPR');
    };

    const shareTimeline = () => {
        const message = timeline.map(event => `${event.timestamp}: ${event.action}`).join('\n');
        if (navigator.share) {
            navigator.share({ text: message }).catch((error) => console.error('Error sharing', error));
        } else {
            alert("Sharing is not supported on this device. Please copy the timeline manually.");
        }
    };

    const emailTimeline = () => {
        const message = timeline.map(event => `${event.timestamp}: ${event.action}`).join('\n');
        fetch('/send-email', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ subject: 'Code Timeline', body: `Here is the code timeline:\n\n${message}` }),
        })
        .then(response => response.json())
        .then(() => alert('Email sent successfully!'))
        .catch((error) => console.error('Error sending email:', error));
    };

    return (
        <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            backgroundColor: (theme) => theme.palette.background.default,
            padding: 2,
        }}>
            <Paper sx={{
                padding: 3,
                borderRadius: 2,
                boxShadow: 3,
                width: '100%',
                maxWidth: 800,
            }}>
                <Typography variant="h4" sx={{ textAlign: 'center', marginBottom: 2 }}>
                    Cardiac Arrest Timer
                </Typography>

                <ArrestTimer isRunning={isRunning} reset={reset} />
                <Divider sx={{ margin: '24px 0' }} />
                <Grid container spacing={2} justifyContent="center">
                    <Grid item xs={6} md={3}>
                        <EpinephrineTimer isCPR={isCPR} />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <PulseCheckTimer isCPR={isCPR} />
                    </Grid>
                </Grid>

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                    marginTop: 2,
                }}>
                    <Button variant="contained" color="primary" onClick={startTimer} startIcon={<FontAwesomeIcon icon={faPlay} />}>
                        Start Code
                    </Button>
                    <Button variant="contained" color="secondary" onClick={stopTimer} startIcon={<FontAwesomeIcon icon={faStop} />}>
                        Stop Code
                    </Button>
                    <Button variant="contained" color="primary" onClick={startCPR} startIcon={<FontAwesomeIcon icon={faHeartbeat} />}>
                        Start CPR
                    </Button>
                    <Button variant="contained" color="secondary" onClick={stopCPR} startIcon={<FontAwesomeIcon icon={faStop} />}>
                        Stop CPR
                    </Button>
                    <Button variant="outlined" onClick={resetTimer} startIcon={<FontAwesomeIcon icon={faRedo} />}>
                        Reset
                    </Button>
                </Box>

                <Divider sx={{ margin: '24px 0' }} />
                <Actions addTimelineEvent={addTimelineEvent} />

                <Box sx={{ marginTop: 3 }}>
                    <Typography variant="h6">Timeline</Typography>
                    <ul>
                        {timeline.map((event, index) => (
                            <li key={index}>
                                <strong>{event.timestamp}:</strong> {event.action}
                            </li>
                        ))}
                    </ul>
                </Box>

                <Divider sx={{ margin: '24px 0' }} />
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                }}>
                    <Button variant="outlined" color="primary" onClick={shareTimeline} startIcon={<FontAwesomeIcon icon={faShareAlt} />}>
                        Share Timeline
                    </Button>
                    <Button variant="outlined" color="secondary" onClick={emailTimeline} startIcon={<FontAwesomeIcon icon={faEnvelope} />}>
                        Complete Code
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
}
